import { lazy, Suspense, useMemo, type ReactNode } from "react";
import { downloadText } from "../../download";
import { chartCsv, chartOptions, unitFormatter, type ChartSeries, type ChartUnit } from "./chart";

/** O ApexCharts (≈500 kB) só baixa quando uma tela de dashboard abre. */
const Chart = lazy(() => import("react-apexcharts"));

type Props = {
  title: string;
  type?: "line" | "bar" | "area";
  categories: string[];
  series: ChartSeries[];
  unit: ChartUnit;
  /** Nome dos arquivos baixados (sem extensão). */
  filename: string;
  stacked?: boolean;
  height?: number;
  actions?: ReactNode;
  testId?: string;
};

/**
 * Gráfico do dashboard (box do Coup): ApexCharts com o menu Baixar SVG/PNG, botão Baixar CSV (gerado no app, com
 * fórmula neutralizada) e, para leitor de tela e conferência, os mesmos dados numa tabela recolhível.
 */
export function ChartCard({
  title,
  type = "line",
  categories,
  series,
  unit,
  filename,
  stacked,
  height = 300,
  actions,
  testId,
}: Props) {
  const options = useMemo(
    () => chartOptions({ type, categories, unit, filename, stacked }),
    [type, categories, unit, filename, stacked],
  );
  const fmt = unitFormatter(unit);
  return (
    <section className="box card h-100" aria-label={title} data-testid={testId}>
      <div className="card-header d-flex align-items-center justify-content-between gap-2">
        <h2 className="h6 mb-0">{title}</h2>
        {actions}
      </div>
      <div className="card-body">
        <Suspense
          fallback={
            <p className="text-muted mb-0" role="status" style={{ minHeight: height }}>
              Carregando gráfico…
            </p>
          }
        >
          <div aria-hidden="true">
            <Chart type={type} series={series} options={options} height={height} />
          </div>
        </Suspense>
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mt-2">
          <details className="flex-grow-1" style={{ minWidth: 0 }}>
            <summary className="small text-muted">Ver dados em tabela</summary>
            <div className="table-responsive">
              <table className="table table-sm mb-0" aria-label={`${title}: dados`}>
                <thead>
                  <tr>
                    <th scope="col">Categoria</th>
                    {series.map((s) => (
                      <th key={s.name} scope="col" className="text-end">
                        {s.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c, i) => (
                    <tr key={`${c}-${i}`}>
                      <th scope="row" className="fw-normal">
                        {c}
                      </th>
                      {series.map((s) => (
                        <td key={s.name} className="text-end">
                          {fmt(s.data[i])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            aria-label={`Baixar CSV: ${title}`}
            onClick={() => downloadText(`${filename}.csv`, chartCsv(categories, series, unit))}
          >
            Baixar CSV
          </button>
        </div>
      </div>
    </section>
  );
}
